import { useRef, useState, useEffect } from 'react';
import { DialogContent, Button, Divider, Paper, Tooltip, TextField, Grid, Box, IconButton } from '@mui/material';
import { RemoveCircleOutline, Save, RequestQuoteOutlined } from '@mui/icons-material';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	useMoleThemeColor,
} from '../../components/MoleDialogShell';
import { updateActiveTask, Util } from '../../util/Util';
import { mapTasksWithActivation, refreshEmptyActiveTaskDate } from '../../util/taskActivation';
import { loadPendingTasks, persistPendingTasks, revivePendingItems, useSkipFirstRun } from '../../util/pendingTaskStorage';
import { flushItemTableItems } from '../../util/itemTableFlush';
import ConfirmDialog from '../../components/ConfirmDialog';
import { Item } from '../../util/Item';
import OfferDetail from './OfferDetail';
import TaskPane from '../../components/TaskPane';
import OfferItemDetail from './OfferItemDetail';
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'react-toastify';
import { OfferTask, useOffer } from './OfferContext';
import { useUI } from '../../providers/MoleUIContext';
import { LocalStorageKeys, MYSQL_LIMITS } from '../../util/constants';
import { useAppSocket } from '../../providers/AppSocketContext';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import ItemTable from '../../components/ItemTable';
import PosAliasEditCell from '../../components/PosAliasEditCell';
import { applySavedCatalogOption, CatalogItemPickerProvider } from '../../components/CatalogItemActions';
import { v4 } from 'uuid';
import { OFFER_KINDS, type OfferKind, type OfferRow } from './offerTypes';

import { addMoney } from "../../util/quantity";
import { contactToPayload } from '../client/contactBlock';
import { useEphemeralEditTask } from "../../hooks/useEphemeralEditTask";
interface Props {
	isOpen: boolean;
	toggle: () => void;
	bundle?: OfferRow | null;
	onSuccess?: () => void;
}

const OfferModal = ({ isOpen, toggle, bundle, onSuccess }: Props) => {
	const [taskHash, setTaskHash] = useState<string | null>(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [renderChildren, setRenderChildren] = useState(false);
	const [tasksOpen, setTasksOpen] = useState(false);
	const [validateDetail, setValidateDetail] = useState(false);

	const { api, session, access } = useAuth();
	const { setLoadingMessage, queueReRun, t } = useUI();
	const { pendingTasks, setPendingTasks, fetchedTask, setFetchedTask, activeTask, addNewTask, setItems, items, apiRef } = useOffer();

	// An existing document's draft is scratch work: it must not survive the
	// modal, saved or not, nor reach storage with the real drafts.
	useEphemeralEditTask({
		editing: Boolean(bundle),
		tasks: pendingTasks,
		setTasks: setPendingTasks,
	});
	const { appSettings } = useAppSocket();
	const themeColor = useMoleThemeColor();
	const currency = appSettings?.BIZ_CURRENCY?.value || 'USD';
	const prevTaskIdRef = useRef<string | null>(null);
	const validateBtnRef = useRef<HTMLButtonElement>(null);
	const changeRef = useRef<boolean>(false);
	const skipFirstPersist = useSkipFirstRun();
	const locked = Boolean(bundle && String(bundle.status || '') !== 'OPEN');

	const reviveOfferTask = (task: any): OfferTask<Item> =>
		refreshEmptyActiveTaskDate({
			...task,
			kind: (OFFER_KINDS as readonly string[]).includes(task.kind) ? task.kind : OFFER_KINDS[0],
			createdAt: new Date(task.createdAt),
			updatedAt: new Date(task.updatedAt),
			items: revivePendingItems(task.items, (item: any) => new Item(item)),
			contact: task.contact ?? null,
			total: function () {
				return (this.items ?? []).reduce((sum: number, item: { total: number }) => addMoney(sum, item.total), 0);
			},
		});

	const { createdAt, updatedAt, active, items: itms, ...bundleDetails } = activeTask || {};
	const bundleItems = itms ? Object.values(itms).map(({ ...rest }) => rest) : [];
	const canValidate = Boolean(
		activeTask
		&& (activeTask.items || []).length > 0
		&& validateDetail
		&& activeTask.date
		&& activeTask.reference
		&& !locked
		&& (bundle ? taskHash !== Util.hashObject(JSON.stringify(bundleDetails) + JSON.stringify(bundleItems)) : true)
	);

	const fetchData = async () => {
		if (!bundle?.batch) return;
		setLoadingMessage('FETCH');
		const res = await api.get(`/offers/${bundle.batch}`);
		if (res?.status === 200) {
			setFetchedTask(res.data);
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(fetchData);
		}
		setLoadingMessage(null);
	};

	const onSubmit = () => {
		setConfirmModalOpen(true);
	};

	const handleConfirm = async () => {
		if (!activeTask) return;
		if (!Util.checkDating(activeTask.date, access, 'offer')) return;

		const batch = bundle ? activeTask.id : v4();
		const taskItems = flushItemTableItems<Item>(apiRef) ?? activeTask.items!;
		const payload = {
			details: {
				id: batch,
				kind: activeTask.kind as OfferKind,
				receiver: activeTask.receiver,
				agent: activeTask.agent > 0 ? activeTask.agent : null,
				doc_date: activeTask.date,
				valid_until: activeTask.valid_until || null,
				ref: activeTask.reference,
				description: activeTask.description || null,
			},
			records: taskItems.map((item, index) => ({
				item: item.id,
				item_desc: item.item,
				planned_qty: item.quantity,
				amount: item.price,
				sort_order: index,
			})),
			...(contactToPayload(activeTask.contact) ? { contact: contactToPayload(activeTask.contact) } : {}),
		};

		setLoadingMessage(bundle ? 'UPDATE' : 'SAVE');
		const res = bundle
			? await api.put(`/offers/${batch}`, payload)
			: await api.post('/offers', payload);

		if (res?.status === 201 || res?.status === 204) {
			toast.success(t(bundle ? 'common:toast.success.offerUpdate' : 'common:toast.success.offer'), { autoClose: 2500 });
			if (bundle) {
				toggle();
			} else {
				deleteTask(activeTask.id);
			}
			changeRef.current = true;
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(handleConfirm);
		}
		setLoadingMessage(null);
	};

	const deleteTask = (taskId: string) => {
		setPendingTasks((prevTasks) => prevTasks.filter((task) => task.id !== taskId));
	};

	useEffect(() => {
		if (isOpen) {
			if (bundle) {
				addNewTask();
			} else {
				const parsedTasks = loadPendingTasks(LocalStorageKeys.OFFER_TASKS, reviveOfferTask, session?.user_id);
				if (parsedTasks) {
					setPendingTasks(parsedTasks);
					return;
				}
				addNewTask();
			}
		}
	}, [isOpen]);

	useEffect(() => {
		if (isOpen && !bundle) {
			const handleKeyDown = (e: KeyboardEvent) => {
				if (e.ctrlKey && e.altKey && e.key.toLowerCase() === 'q') {
					e.preventDefault();
					setTasksOpen((open) => !open);
				} else if (e.ctrlKey && e.altKey && e.key.toLowerCase() === 's') {
					e.preventDefault();
					validateBtnRef.current?.click();
				}
			};
			document.addEventListener('keydown', handleKeyDown);
			return () => document.removeEventListener('keydown', handleKeyDown);
		}
	}, [isOpen, bundle]);

	useEffect(() => {
		if (skipFirstPersist()) return;
		if (!bundle && isOpen) {
			if (pendingTasks.length === 0) {
				addNewTask();
				return;
			}
			persistPendingTasks(LocalStorageKeys.OFFER_TASKS, session?.user_id, JSON.stringify(pendingTasks));
		}
		if (pendingTasks.length > 0 && !activeTask) {
			setPendingTasks((prevTasks) =>
				mapTasksWithActivation(prevTasks, (_task, index) => index === 0)
			);
		}
	}, [pendingTasks]);

	useEffect(() => {
		if (activeTask) {
			if (!renderChildren) setRenderChildren(true);
			if (prevTaskIdRef.current !== activeTask.id) {
				if (bundle) void fetchData();
				prevTaskIdRef.current = activeTask.id;
			}
			if (bundle && activeTask && bundleItems.length > 0 && !taskHash) {
				setTaskHash(Util.hashObject(JSON.stringify(bundleDetails) + JSON.stringify(bundleItems)));
			}
		} else {
			prevTaskIdRef.current = null;
		}
	}, [activeTask]);

	const cleanup = () => {
		setPendingTasks([]);
		setFetchedTask(null);
		setItems([]);
		setTaskHash(null);
		setRenderChildren(false);
		if (changeRef.current) {
			onSuccess?.();
			changeRef.current = false;
		}
	};

	const toggleDrawer = (newOpen: boolean) => () => {
		setTasksOpen(newOpen);
	};

	const onDeleteClicked = (uniqueId: string) => {
		updateActiveTask(setPendingTasks, (prev) => ({
			items: prev.items ? prev.items.filter((item) => item.uniqueId !== uniqueId) : [],
		}));
	};

	const columns: GridColDef<Item>[] = [
		{
			field: 'rowNumber',
			headerName: '#',
			width: 20,
			valueGetter: (_, row) => apiRef.current!.getRowIndexRelativeToVisibleRows(row.uniqueId) + 1,
		},
		...(Util.isBoolSettingEnabled(appSettings?.UI_SHOW_ITEM_CODE?.value)
			? [{ field: 'item_code', headerName: t('common:tables.itemCode') }]
			: []),
		{
			field: 'item',
			headerName: t('common:tables.item'),
			flex: 2,
			minWidth: 220,
			editable: !locked,
			...(!locked
				? {
					renderEditCell: (params: any) => (
						<PosAliasEditCell
							{...params}
							catalogItems={items}
						/>
					),
				}
				: {}),
		},
		{
			field: 'quantity',
			headerName: t('common:tables.quantity'),
			type: 'number',
			editable: !locked,
		},
		{
			field: 'price',
			headerName: t('common:tables.sellingPrice'),
			type: 'number',
			editable: !locked,
		},
		{
			field: 'total',
			headerName: t('common:tables.lineTotal'),
			type: 'number',
			flex: 1,
			minWidth: 150,
			maxWidth: 150,
		},
		{
			field: 'actions',
			headerName: '',
			sortable: false,
			disableColumnMenu: true,
			renderCell: (params: GridRenderCellParams<Item>) => (
				locked ? null : (
					<Tooltip title={t('common:ui.tooltips.delete')}>
						<IconButton color="error" onClick={() => { onDeleteClicked(params.row.uniqueId); }}>
							<RemoveCircleOutline />
						</IconButton>
					</Tooltip>
				)
			),
		},
	];

	return (
		<Box>
			<MoleDialog
				open={isOpen}
				onClose={toggle}
				maxWidth="xl"
				fullWidth
				themeColor={themeColor}
				paperOptions={{ maxWidth: '95%', width: '95%', maxHeight: '95vh' }}
				disableEscapeKeyDown
				onTransitionExited={cleanup}
			>
				<MoleDialogHeader
					title={bundle ? t('common:offer.editTitle') : t('common:offer.modalTitle')}
					subtitle={`${t('common:report.grandTotal')}: ${Util.formatCurrency(activeTask?.total() || 0, currency)}`}
					icon={<RequestQuoteOutlined />}
					onClose={toggle}
					themeColor={themeColor}
					extra={
						!bundle ? (
							<Button
								variant="outlined"
								size="small"
								startIcon={<PendingActionsIcon />}
								onClick={() => toggleDrawer(true)()}
							>
								{t('common:ui.buttons.tasks')}
							</Button>
						) : undefined
					}
				/>
				<DialogContent>
					<Paper elevation={24}>
						{renderChildren &&
							<OfferDetail setValidateCheck={setValidateDetail} locked={locked} />
						}
						<Divider orientation="vertical" variant="middle" flexItem />
						{renderChildren && !locked &&
							<OfferItemDetail locked={locked} />
						}
					</Paper>
					{renderChildren &&
						<CatalogItemPickerProvider
							onItemSaved={(updated) => {
								setItems(applySavedCatalogOption(items, updated));
								updateActiveTask(setPendingTasks, (prev) => ({
									...prev,
									items: prev.items?.map((it) =>
										it.id === updated.id
											? new Item({
												...it,
												item: updated.ItemName || it.item,
												item_code: updated.ItemCode || it.item_code,
												isFractional: updated.IsFractional === true,
											})
											: it
									),
								}));
							}}
						>
							<ItemTable<Item, OfferTask<Item>>
								activeTask={activeTask}
								setPendingTasks={setPendingTasks}
								columns={columns}
								itemConstructor={Item}
								bundle={fetchedTask?.records}
								items={items}
								checkStock={false}
								lineNameMode="sale"
								allowPosAliasing={!locked}
								apiRef={apiRef}
							/>
						</CatalogItemPickerProvider>
					}
				</DialogContent>
				<MoleDialogActions sx={{ justifyContent: 'stretch' }}>
					<Grid container spacing={1} alignItems="center" width="100%">
						<Grid sx={{ flexGrow: 1, minWidth: 0 }}>
							<TextField
								id="details"
								size="small"
								label={t('common:ui.labels.notes')}
								variant="filled"
								fullWidth
								type="text"
								value={activeTask?.description || ''}
								inputProps={{ maxLength: MYSQL_LIMITS.TEXT.maxLength }}
								onChange={(e) => updateActiveTask(setPendingTasks, { description: e.target.value })}
								disabled={!activeTask || locked}
							/>
						</Grid>
						<Grid
							sx={{
								width: { xs: '100%', sm: '100%', md: 'auto' },
								display: 'flex',
								justifyContent: { xs: 'space-between', md: 'flex-end' },
								alignItems: 'center',
								gap: 1,
							}}
						>
							<Button
								ref={validateBtnRef}
								variant="contained"
								color="success"
								onClick={onSubmit}
								disabled={!canValidate}
								startIcon={<Save />}
							>
								{t('common:ui.buttons.save')}
							</Button>
							<MoleCancelButton onClick={toggle}>{t('common:ui.buttons.cancel')}</MoleCancelButton>
						</Grid>
					</Grid>
				</MoleDialogActions>
				{!bundle &&
					<TaskPane<Item, OfferTask<Item>>
						tasks={pendingTasks}
						setTasks={setPendingTasks}
						addNewTask={addNewTask}
						tasksOpen={tasksOpen}
						onCallback={() => setTasksOpen(false)}
						onDeleteTask={deleteTask}
					/>
				}
			</MoleDialog>
			<ConfirmDialog
				onConfirm={handleConfirm}
				isOpen={confirmModalOpen}
				setIsOpen={setConfirmModalOpen}
				title={t('common:dialog.documentConfirmation')}
				confirmBtnColor="success"
			/>
		</Box>
	);
};

export default OfferModal;
